import React, { useRef } from 'react';
import './NativeDateField.css';

export function isoToMDY(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return '';
  return `${m}-${d}-${y}`;
}

export function mdyToISO(mdy) {
  const [m, d, y] = (mdy || '').split('-');
  if (!m || !d || !y) return '';
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

function isIosDevice() {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

const defaultInputClass =
  'h-10 w-full rounded-md border border-gray-300 px-3 pr-10 text-sm text-[#2C2C2C] bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500';

const SIZE_CLASSES = {
  // Matches sibling readonly fields (e.g. Project No. / Activity in Crew Timesheet)
  md: {
    wrap: 'relative h-10 w-full',
    inputForce: 'h-10',
  },
  // Default: slightly taller for touch-friendly hit area
  lg: {
    wrap: 'relative h-11 w-full min-h-[44px]',
    inputForce: 'h-11 min-h-[44px]',
  },
};

/**
 * Cross-platform date field (iOS Safari + Android + desktop).
 * Visible formatted value + native type="date" hit target.
 * Skips showPicker() on iOS — it mis-positions inside portaled / fixed modals.
 *
 * @param {'md'|'lg'} [size='lg'] - md = h-10 (match form fields); lg = h-11 touch target
 */
export default function NativeDateField({
  label,
  value = '',
  onChange,
  labelClassName = 'text-[14px] font-normal text-[#424143] font-poppins',
  inputClassName = defaultInputClass,
  placeholder = 'MM-DD-YYYY',
  className = '',
  required = false,
  formatDisplay = isoToMDY,
  disabled = false,
  size = 'lg',
}) {
  const dateRef = useRef(null);
  const isIos = isIosDevice();
  const sizeClasses = SIZE_CLASSES[size] || SIZE_CLASSES.lg;

  const openPicker = () => {
    if (disabled) return;

    const input = dateRef.current;
    if (!input) return;

    // showPicker can place the calendar in the wrong spot inside fullscreen portals on iOS.
    if (!isIos && typeof input.showPicker === 'function') {
      try {
        input.showPicker();
        return;
      } catch {
        // Fall through
      }
    }

    input.focus({ preventScroll: true });
    input.click();
  };

  const handleDateChange = (nextValue) => {
    if (nextValue && /^\d{4}-\d{2}-\d{2}$/.test(nextValue)) {
      onChange?.(nextValue);
    }
  };

  return (
    <div className={`native-date-field flex flex-col gap-1 ${className}`}>
      {label ? <label className={labelClassName}>{label}</label> : null}
      <div className={sizeClasses.wrap}>
        <input
          type="text"
          readOnly
          required={required}
          placeholder={placeholder}
          value={formatDisplay(value)}
          disabled={disabled}
          className={`${inputClassName} ${sizeClasses.inputForce} pointer-events-none`}
          aria-hidden="true"
          tabIndex={-1}
        />
        {/*
          Keep a tiny opacity (>0). Fully invisible controls are sometimes ignored by iOS Safari taps.
          font-size 16px prevents iOS focus zoom.
        */}
        <input
          ref={dateRef}
          type="date"
          value={value || ''}
          disabled={disabled}
          onChange={(e) => handleDateChange(e.target.value)}
          onClick={(e) => {
            // Desktop Chrome: open via showPicker when available.
            // iOS: let the native control handle the tap; only force-open if needed.
            if (!isIos) {
              e.preventDefault();
              openPicker();
            }
          }}
          className="native-date-field__hit absolute inset-0 z-[2] h-full w-full cursor-pointer"
          style={{
            opacity: 0.02,
            fontSize: 16,
            colorScheme: 'light',
            WebkitAppearance: 'none',
            appearance: 'none',
          }}
          aria-label={label || 'Date'}
          required={required}
        />
        <span
          className="pointer-events-none absolute right-3 top-1/2 z-[1] -translate-y-1/2 text-gray-400"
          aria-hidden="true"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M7 2V5M17 2V5M3 9H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>
    </div>
  );
}
